// frontend/src/components/BetaFeatureModal.tsx
import { useNavigate } from 'react-router-dom'
import { X, Sparkles, Zap, FlaskConical } from 'lucide-react'
import { useBetaFeatures } from '@/hooks/useBetaFeatures'

const BADGE_STYLES: Record<string, string> = {
    New: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    Beta: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
    'Early Access': 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
}

const BADGE_ICONS: Record<string, React.ReactNode> = {
    New: <Sparkles className="w-3 h-3" />,
    Beta: <FlaskConical className="w-3 h-3" />,
    'Early Access': <Zap className="w-3 h-3" />,
}

export function BetaFeatureModal() {
    const { current, dismiss } = useBetaFeatures()
    const navigate = useNavigate()

    if (!current) return null

    const badgeStyle = BADGE_STYLES[current.badge_text] ?? BADGE_STYLES['New']
    const badgeIcon = BADGE_ICONS[current.badge_text] ?? BADGE_ICONS['New']

    const handleCta = () => {
        dismiss(true)
        if (current.cta_url) {
            // Internal links (start with /) use router, external open in new tab
            if (current.cta_url.startsWith('/')) {
                navigate(current.cta_url)
            } else {
                window.open(current.cta_url, '_blank', 'noopener')
            }
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={() => dismiss()}
            />

            {/* Modal */}
            <div className="relative w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl p-6 animate-in fade-in zoom-in-95 duration-200">
                {/* Close */}
                <button
                    onClick={() => dismiss()}
                    className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                    aria-label="Dismiss"
                >
                    <X className="w-4 h-4" />
                </button>

                {/* Badge */}
                <div className="mb-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${badgeStyle}`}>
                        {badgeIcon}
                        {current.badge_text}
                    </span>
                </div>

                {/* Content */}
                <h2 className="text-xl font-bold text-foreground mb-3 pr-6">
                    {current.title}
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                    {current.description}
                </p>

                {/* Actions */}
                <div className="flex items-center gap-3">
                    {current.cta_url && (
                        <button
                            onClick={handleCta}
                            className="flex-1 btn-gradient px-4 py-2.5 text-sm font-semibold rounded-lg"
                        >
                            {current.cta_label}
                        </button>
                    )}
                    <button
                        onClick={() => dismiss()}
                        className={`px-4 py-2.5 text-sm font-medium rounded-lg border border-border hover:bg-muted transition-colors ${current.cta_url ? '' : 'flex-1'
                            }`}
                    >
                        {current.cta_url ? 'Maybe later' : 'Got it'}
                    </button>
                </div>
            </div>
        </div>
    )
}